// src/components/winback/CampaignHistoryTable.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faClock, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { get } from '../../helpers/apiHelper';
import SegmentBadge from './SegmentBadge';
import Select from 'react-select';

// Estilos personalizados para react-select con z-index alto
const selectStyles = {
    menu: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    })
};

const CampaignHistoryTable = ({ marcaId }) => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState(null);

    useEffect(() => {
        const loadCampaigns = async () => {
            if (!marcaId) return;

            setLoading(true);
            try {
                const endpoint = statusFilter
                    ? `clientes/winback/campaigns?marcaId=${marcaId}&status=${statusFilter}`
                    : `clientes/winback/campaigns?marcaId=${marcaId}`;

                const data = await get(endpoint);
                setCampaigns(data);
            } catch (error) {
                console.error('Error loading campaigns:', error);
                setCampaigns([]);
            } finally {
                setLoading(false);
            }
        };

        loadCampaigns();
    }, [marcaId, statusFilter]);

    const statusOptions = [
        { value: null, label: 'Todos los estados' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'sent', label: 'Enviado' },
        { value: 'converted', label: 'Convertido' }
    ];

    const getStatusBadge = (status) => {
        const configs = {
            pending: { icon: faClock, bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
            sent: { icon: faPaperPlane, bg: 'bg-blue-100', text: 'text-blue-800', label: 'Enviado' },
            converted: { icon: faCheckCircle, bg: 'bg-green-100', text: 'text-green-800', label: 'Convertido' }
        };

        const config = configs[status] || configs.pending;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                <FontAwesomeIcon icon={config.icon} className="mr-1" />
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <div className="spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full text-purple-600 mb-4"></div>
                        <p className="text-gray-600">Cargando historial...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (campaigns.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faPaperPlane} className="text-gray-400 text-2xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No hay campañas registradas
                </h3>
                <p className="text-gray-500">
                    Las campañas de WinBack aparecerán aquí una vez que se envíen
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Historial de Campañas
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {campaigns.length} campaña{campaigns.length !== 1 ? 's' : ''} registrada{campaigns.length !== 1 ? 's' : ''}
                        </p>
                    </div>

                    <div className="w-full sm:w-48">
                        <Select
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === statusFilter)}
                            onChange={(option) => setStatusFilter(option.value)}
                            placeholder="Filtrar por estado"
                            className="text-sm"
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Cliente
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Fecha Envío
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Días Inactivo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Conversión
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Tiempo Conversión
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {campaigns.map((campaign) => {
                            const cliente = typeof campaign.cliente === 'object' ? campaign.cliente : null;
                            const daysToConvert = campaign.convertedAt && campaign.sentAt
                                ? Math.round((new Date(campaign.convertedAt) - new Date(campaign.sentAt)) / (1000 * 60 * 60 * 24))
                                : null;

                            return (
                                <tr key={campaign._id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {cliente ? `${cliente.nombre} ${cliente.apellido}` : 'Cliente no disponible'}
                                        </div>
                                        {cliente && cliente.email && (
                                            <div className="text-xs text-gray-500">
                                                {cliente.email}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {campaign.sentAt ? new Date(campaign.sentAt).toLocaleDateString() : 'No enviado'}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {campaign.sentAt ? new Date(campaign.sentAt).toLocaleTimeString() : ''}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {campaign.daysInactive} días
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(campaign.status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {campaign.convertedAt ? (
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xl" />
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {daysToConvert !== null ? (
                                            <div className="text-sm font-medium text-green-600">
                                                {daysToConvert} día{daysToConvert !== 1 ? 's' : ''}
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400">-</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CampaignHistoryTable;

